'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { MarketingDisplayH1 } from '@/components/marketing/MarketingDisplayH1'
import { MarketingNav } from '@/components/marketing/MarketingNav'
import { HomepageFooter } from '@/components/marketing/HomepageFooter'
import HeroBackground from '@/components/marketing/HeroBackgroundDynamic'

// ── Device frames ─────────────────────────────────────────────────────────────

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative shrink-0" style={{ width: 252 }}>
      <div className="rounded-[32px] overflow-hidden" style={{ border: '2px solid rgba(19,32,27,0.18)', background: '#13201B', boxShadow: '0 24px 60px rgba(0,0,0,0.22)', height: 520 }}>
        <div className="h-7 flex items-center justify-center shrink-0" style={{ background: '#07090F' }}>
          <div className="w-14 h-2 rounded-full bg-black" />
        </div>
        <div style={{ height: 'calc(100% - 28px)', background: '#fff', overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </div>
  )
}

function DesktopFrame({ active, children }: { active: string; children: React.ReactNode }) {
  const tabs = ['Game Day', 'Roster', 'Schedule', 'Payments', 'Chat']
  return (
    <div className="flex-1 rounded-xl overflow-hidden" style={{ boxShadow: '0 16px 48px rgba(0,0,0,0.12)', border: '1px solid #E5E9F0', height: 520, maxWidth: 520 }}>
      <div className="h-8 flex items-center px-3 gap-2 shrink-0" style={{ background: '#F7F8FA', borderBottom: '1px solid #EAECF0' }}>
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#FFBC2E]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28CA41]" />
        </div>
        <div className="flex-1 text-center text-[10px] font-medium text-slate-600">chalk · Chosen Gold</div>
      </div>
      <div className="flex" style={{ height: 492 }}>
        <div className="w-28 shrink-0 bg-[#FAFBFC] border-r border-[#F0F2F5] pt-3 pb-3 flex flex-col">
          <div className="px-3 mb-2.5">
            <div className="text-[7px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Team</div>
            <div className="text-[10px] font-black text-app-card">Chosen Gold</div>
            <div className="text-[8px] text-slate-500">12 players</div>
          </div>
          <div className="px-2 space-y-0.5 flex-1">
            {tabs.map(t => (
              <div key={t} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[9px] font-medium" style={{ background: active === t ? '#13201B' : 'transparent', color: active === t ? 'white' : '#5b6776' }}>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  )
}

// ── Sticky sub-nav ────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'game-day', label: 'Game Day' },
  { id: 'roster',   label: 'Roster'   },
  { id: 'schedule', label: 'Schedule' },
  { id: 'payments', label: 'Payments' },
  { id: 'chat',     label: 'Chat'     },
]

function SubNav() {
  const [active, setActive] = useState('game-day')
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id) }),
      { rootMargin: '-40% 0px -50% 0px' }
    )
    SECTIONS.forEach(s => { const el = document.getElementById(s.id); if (el) obs.observe(el) })
    return () => obs.disconnect()
  }, [])
  return (
    <div className="sticky z-40 bg-white border-b border-[#EAECF0]" style={{ top: 64 }}>
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="flex items-center gap-1 py-1 overflow-x-auto">
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="shrink-0 px-4 py-2 rounded-full text-[13px] font-semibold transition-all"
              style={{ background: active === s.id ? '#13201B' : 'transparent', color: active === s.id ? '#fff' : '#637082' }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Bullet list ───────────────────────────────────────────────────────────────

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 mb-7">
      {items.map(b => (
        <li key={b} className="flex items-start gap-2.5 text-[13px] text-app-card leading-snug">
          <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: '#DCFCE7' }}>
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4l2 2L6.5 2.5" stroke="#166534" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          {b}
        </li>
      ))}
    </ul>
  )
}

// ── Split demo wrapper ────────────────────────────────────────────────────────

function Split({ active, phone, desktop }: { active: string; phone: React.ReactNode; desktop: React.ReactNode }) {
  return (
    <div className="flex-1 flex items-center justify-center gap-4 w-full max-w-[820px]">
      <PhoneFrame>{phone}</PhoneFrame>
      <DesktopFrame active={active}>{desktop}</DesktopFrame>
    </div>
  )
}

// ── In-app demo mocks — live React mockups rendered in the Split frames above.
// These replaced static PNG screenshots (which had drifted to show killed
// products + a dev-error overlay). Game Day is a simple +/- scoreboard.

function GameDayPhone() {
  // Simple +/- TEAM scoreboard (Game Day is demoted to a clean scoreboard —
  // no per-player stats, pitch counts, or play-by-play). BSO is a live in-game
  // count for baseball/softball (kept per owner directive #294), shown as a
  // read-only display, not stat tracking. Surface is the Field Mode dark #07090F.
  const [home, setHome] = useState(3)
  const [away, setAway] = useState(3)
  return (
    <div className="h-full flex flex-col p-2.5" style={{ background: '#07090F' }}>
      <div className="flex items-center gap-1.5 mb-2">
        <div className="w-1.5 h-1.5 rounded-full bg-[#FF4B4B] animate-pulse" />
        <span className="text-[7px] font-bold text-[#FF4B4B] uppercase tracking-wider">Live · Bot 5</span>
      </div>
      <div className="rounded-xl p-2.5 mb-2 flex items-center justify-around" style={{ background: 'rgba(255,255,255,0.04)' }}>
        <div className="text-center">
          <div className="text-[6px] text-white/35 uppercase tracking-wider mb-0.5">Rivals</div>
          <div className="text-[30px] font-black text-white/80 leading-none" style={{ fontFamily: 'var(--font-bebas)' }}>{away}</div>
          <button onClick={() => setAway(a => a + 1)} aria-label="Add run for Rivals" className="mt-1.5 w-6 h-6 rounded-full text-[13px] font-black text-white/70 active:scale-90 transition-all" style={{ background: 'rgba(255,255,255,0.08)' }}>+</button>
        </div>
        <div className="text-white/10">—</div>
        <div className="text-center">
          <div className="text-[6px] text-white/35 uppercase tracking-wider mb-0.5">Chosen</div>
          <div className="text-[30px] font-black leading-none transition-all" style={{ fontFamily: 'var(--font-bebas)', color: '#F5E642' }}>{home}</div>
          <button onClick={() => setHome(h => h + 1)} aria-label="Add run for Chosen" className="mt-1.5 w-6 h-6 rounded-full text-[13px] font-black text-chalk-dark active:scale-90 transition-all" style={{ background: '#F5E642' }}>+</button>
        </div>
      </div>
      <div className="flex gap-1.5 mt-auto">
        {([['B', 2, 3, '#52D68A'], ['S', 1, 2, '#f87171'], ['O', 1, 2, '#ffffff']] as const).map(([l, v, m, c]) => (
          <div key={l} className="flex-1 rounded-lg p-1.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <div className="text-[6px] text-white/25 uppercase mb-1">{l}</div>
            <div className="flex gap-0.5">{Array.from({ length: m }, (_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: i < v ? c : 'rgba(255,255,255,0.1)' }} />)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function GameDayDesktop() {
  // Simple +/- TEAM scoreboard (no per-player pitch count / play-by-play —
  // those are killed). BSO is a read-only live count for baseball (owner #294).
  const [home, setHome] = useState(3)
  const [away, setAway] = useState(3)
  return (
    <div className="p-3 h-full flex flex-col" style={{ background: '#07090F' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-1.5 rounded-full bg-[#FF4B4B] animate-pulse" />
        <span className="text-[8px] font-bold text-[#FF4B4B] uppercase tracking-wider">Live · Bot 5 · Diamond Park</span>
      </div>
      <div className="rounded-xl p-3 mb-3 flex items-center justify-around" style={{ background: 'rgba(255,255,255,0.04)' }}>
        <div className="text-center">
          <div className="text-[7px] text-white/35 uppercase tracking-wider mb-1">Rivals</div>
          <div className="text-[40px] font-black text-white/80 leading-none" style={{ fontFamily: 'var(--font-bebas)' }}>{away}</div>
          <button onClick={() => setAway(a => a + 1)} aria-label="Add run for Rivals" className="mt-2 px-3 py-1 rounded-lg text-[10px] font-black text-white/70 transition-all hover:brightness-110 active:scale-95" style={{ background: 'rgba(255,255,255,0.08)' }}>+ Run</button>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.08)' }}>—</div>
        <div className="text-center">
          <div className="text-[7px] text-white/35 uppercase tracking-wider mb-1">Chosen ★</div>
          <div className="text-[40px] font-black leading-none transition-all" style={{ fontFamily: 'var(--font-bebas)', color: '#F5E642' }}>{home}</div>
          <button onClick={() => setHome(h => h + 1)} aria-label="Add run for Chosen" className="mt-2 px-3 py-1 rounded-lg text-[10px] font-black text-chalk-dark transition-all hover:brightness-110 active:scale-95" style={{ background: '#F5E642' }}>+ Run</button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 mt-auto">
        {([['Balls', 2, 3, '#52D68A'], ['Strikes', 1, 2, '#f87171'], ['Outs', 1, 2, '#ffffff']] as const).map(([l, v, m, c]) => (
          <div key={l} className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="text-[7px] text-white/30 uppercase tracking-wider mb-1">{l}</div>
            <div className="flex gap-1">{Array.from({ length: m }, (_, i) => <div key={i} className="w-2 h-2 rounded-full" style={{ background: i < v ? c : 'rgba(255,255,255,0.1)' }} />)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Roster ────────────────────────────────────────────────────────────────────

const ROSTER = [
  { num: '23', name: 'Marcus Harris',  pos: 'CF', waiver: true  },
  { num: '7',  name: 'Jordan Davis',   pos: 'SS', waiver: true  },
  { num: '11', name: 'Theo Cooper',    pos: 'P',  waiver: true  },
  { num: '5',  name: 'Alex Jackson',   pos: '3B', waiver: false },
  { num: '14', name: 'Brandon Wilson', pos: '1B', waiver: true  },
]

function RosterPhone() {
  const [exp, setExp] = useState<string|null>('23')
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
        <span className="text-[10px] font-black text-slate-800">Roster · 12</span>
        <button className="text-[7px] font-bold bg-chalk-yellow-hover text-chalk-dark rounded-full px-2 py-0.5">+ Add</button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {ROSTER.map(p => (
          <div key={p.num} onClick={() => setExp(exp === p.num ? null : p.num)} className="border-b border-slate-50 cursor-pointer">
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="w-6 h-6 rounded-md flex items-center justify-center text-[7px] font-black text-chalk-dark shrink-0" style={{ background: '#FEF9C3' }}>{p.num}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-semibold text-slate-800 truncate">{p.name}</div>
                <div className="text-[7px] text-slate-500">{p.pos}</div>
              </div>
              {!p.waiver && <div className="text-[6px] font-bold text-amber-600 bg-amber-50 rounded-full px-1.5 py-0.5">Waiver</div>}
            </div>
            {exp === p.num && (
              <div className="bg-slate-50 px-3 py-1.5 border-t border-slate-100">
                <div className="text-[7px] text-slate-500">Guardian · Jersey · Payment history</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function RosterDesktop() {
  return (
    <div className="p-3 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-slate-800">12 players</span>
        <button className="text-[8px] font-semibold bg-chalk-yellow-hover text-chalk-dark rounded-full px-2 py-1">+ Add player</button>
      </div>
      <div className="space-y-1">
        {ROSTER.map(p => (
          <div key={p.num} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-white px-2.5 py-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center text-[7px] font-black text-chalk-dark" style={{ background: '#FEF9C3' }}>{p.num}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[9px] font-semibold text-slate-800 truncate">{p.name}</div>
              <div className="text-[8px] text-slate-500">{p.pos}</div>
            </div>
            {!p.waiver && <div className="text-[7px] font-bold text-amber-600 bg-amber-50 rounded-full px-1.5 py-0.5">Waiver needed</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Schedule ──────────────────────────────────────────────────────────────────

const EVENTS = [
  { d: 'Apr 19', day: 'Sat', title: 'vs Rivals',   time: '9:00 AM',  type: 'game',     going: 10, not: 1 },
  { d: 'Apr 21', day: 'Mon', title: 'Fielding Lab', time: '4:00 PM',  type: 'practice', going: 9,  not: 0 },
  { d: 'Apr 26', day: 'Sat', title: 'vs Eagles',   time: '11:00 AM', type: 'game',     going: 11, not: 1 },
]

function SchedulePhone() {
  const [rsvp, setRsvp] = useState<Record<string,string>>({ 'Apr 19': 'going' })
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
        <span className="text-[10px] font-black text-slate-800">Schedule</span>
        <button className="text-[7px] font-bold bg-chalk-yellow-hover text-chalk-dark rounded-full px-2 py-0.5">+ Add</button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {EVENTS.map(ev => (
          <div key={ev.d} className="rounded-lg border border-slate-100 p-2">
            <div className="flex items-start gap-2">
              <div className="w-8 text-center shrink-0">
                <div className="text-[6px] font-bold text-slate-500 uppercase">{ev.day}</div>
                <div className="text-[14px] font-black text-slate-800 leading-tight">{ev.d.split(' ')[1]}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold text-slate-800">{ev.title}</div>
                <div className="text-[7px] text-slate-500">{ev.time}</div>
                <div className="flex gap-1 mt-1">
                  {(['going','not','?'] as const).map(s => (
                    <button key={s} onClick={() => setRsvp(r => ({...r, [ev.d]: s}))} className="text-[6px] font-bold rounded px-1.5 py-0.5 transition-all"
                      style={{ background: rsvp[ev.d]===s ? s==='going'?'#22c55e':s==='not'?'#ef4444':'#f59e0b' : '#f1f5f9', color: rsvp[ev.d]===s?'white':'#64748b' }}>
                      {s==='going'?'✓':s==='not'?'✗':'?'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ScheduleDesktop() {
  return (
    <div className="p-3 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold text-slate-800">Spring 2026</span>
        <button className="text-[8px] font-semibold bg-chalk-yellow-hover text-chalk-dark rounded-full px-2 py-1">+ Add event</button>
      </div>
      <div className="space-y-1.5">
        {EVENTS.map(ev => (
          <div key={ev.d} className="rounded-lg border border-slate-100 bg-white p-2.5 flex items-start gap-2.5">
            <div className="w-9 text-center shrink-0">
              <div className="text-[6px] font-bold text-slate-500 uppercase">{ev.day}</div>
              <div className="text-[16px] font-black text-slate-800 leading-tight">{ev.d.split(' ')[1]}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 mb-0.5">
                <span className={`text-[7px] font-bold rounded-full px-1.5 py-0.5 ${ev.type==='game'?'bg-green-50 text-green-700':'bg-blue-50 text-blue-700'}`}>{ev.type==='game'?'Game':'Practice'}</span>
              </div>
              <div className="text-[10px] font-bold text-slate-800">{ev.title}</div>
              <div className="text-[8px] text-slate-500">{ev.time}</div>
              <div className="flex gap-2 mt-1 text-[8px]">
                <span className="text-green-600 font-semibold">✓ {ev.going}</span>
                <span className="text-red-400 font-semibold">✗ {ev.not}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Payments ──────────────────────────────────────────────────────────────────

function PaymentsPhone() {
  const [reminded, setReminded] = useState(false)
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-3 py-2 border-b border-slate-100">
        <span className="text-[10px] font-black text-slate-800">Payments</span>
      </div>
      <div className="p-2.5 space-y-2">
        <div className="grid grid-cols-2 gap-1.5">
          <div className="rounded-lg bg-green-50 border border-green-100 p-2">
            <div className="text-[6px] font-bold text-green-600 uppercase mb-0.5">Collected</div>
            <div className="text-[15px] font-black text-green-800">$1,650</div>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-100 p-2">
            <div className="text-[6px] font-bold text-amber-600 uppercase mb-0.5">Owed</div>
            <div className="text-[15px] font-black text-amber-800">$150</div>
          </div>
        </div>
        {[{ id:'d', title:'Spring Season Dues', sub:'$150 · 11/12 paid', pct:91.6 }, { id:'t', title:'Tournament Reg.', sub:'$75 · 8/12 paid', pct:66.7 }].map(r => (
          <div key={r.id} className="rounded-lg border border-slate-100 p-2">
            <div className="flex items-start justify-between mb-1.5">
              <div>
                <div className="text-[8px] font-bold text-slate-800">{r.title}</div>
                <div className="text-[7px] text-slate-500">{r.sub}</div>
              </div>
              {r.id==='d' && (
                <button onClick={() => setReminded(true)} className="text-[6px] font-semibold rounded px-1.5 py-1 transition-all" style={{ background: reminded?'#f0fdf4':'#f1f5f9', color: reminded?'#16a34a':'#475569' }}>
                  {reminded ? '✓ Sent' : 'Remind'}
                </button>
              )}
            </div>
            <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full bg-green-400" style={{ width: `${r.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function PaymentsDesktop() {
  const [reminded, setReminded] = useState<Record<string,boolean>>({})
  return (
    <div className="p-3 h-full overflow-y-auto">
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg bg-green-50 border border-green-100 p-2.5">
          <div className="text-[7px] font-bold text-green-600 uppercase mb-0.5">Collected</div>
          <div className="text-[18px] font-black text-green-800">$1,650</div>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-100 p-2.5">
          <div className="text-[7px] font-bold text-amber-600 uppercase mb-0.5">Outstanding</div>
          <div className="text-[18px] font-black text-amber-800">$150</div>
        </div>
      </div>
      {[{ id:'d', title:'Spring Season Dues', sub:'$150 · 11/12 paid', pct:91.6 }, { id:'t', title:'Tournament Registration', sub:'$75 · 8/12 paid', pct:66.7 }].map(r => (
        <div key={r.id} className="rounded-lg border border-slate-100 bg-white p-2.5 mb-2">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[10px] font-bold text-slate-800">{r.title}</div>
              <div className="text-[8px] text-slate-500">{r.sub}</div>
            </div>
            <button onClick={() => setReminded(v=>({...v,[r.id]:true}))} className="text-[8px] font-semibold rounded-lg px-2 py-1 transition-all" style={{ background: reminded[r.id]?'#f0fdf4':'#f1f5f9', color: reminded[r.id]?'#16a34a':'#475569' }}>
              {reminded[r.id] ? '✓ Reminder sent' : 'Send reminder'}
            </button>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-green-400 transition-all" style={{ width: `${r.pct}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Chat ──────────────────────────────────────────────────────────────────────

const MSGS_INIT = [
  { from: 'Coach Hern', init: 'CH', coach: true,  text: 'Game tomorrow at Diamond Park — arrive by 8:30 AM.', time: '6:42 PM' },
  { from: 'Sarah H.',   init: 'SH', coach: false, text: 'Marcus will be there!', time: '6:58 PM' },
  { from: 'Mike D.',    init: 'MD', coach: false, text: 'Jordan can ride with the Coopers', time: '7:03 PM' },
  { from: 'Coach Hern', init: 'CH', coach: true,  text: 'Perfect. See everyone at 8:30!', time: '7:10 PM' },
]

function ChatPhone() {
  const [msgs, setMsgs] = useState(MSGS_INIT)
  const [draft, setDraft] = useState('')
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-chalkboard flex items-center justify-center text-[6px] font-bold text-white">CG</div>
        <div className="text-[9px] font-bold text-slate-800">Chosen Gold Chat</div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5">
        {msgs.map((m,i) => (
          <div key={i} className={`flex gap-1.5 ${m.from==='You'?'flex-row-reverse':''}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[5px] font-bold shrink-0 ${m.coach?'bg-chalkboard text-white':'bg-slate-200 text-slate-600'}`}>{m.init}</div>
            <div className={`max-w-[72%] rounded-xl px-2 py-1 text-[7px] leading-snug ${m.from==='You'?'bg-chalkboard text-white':'bg-slate-100 text-slate-800'}`}>{m.text}</div>
          </div>
        ))}
      </div>
      <div className="px-2 py-1.5 border-t border-slate-100 flex gap-1">
        <input value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&draft.trim()){setMsgs(m=>[...m,{from:'You',init:'ME',coach:true,text:draft.trim(),time:'Now'}]);setDraft('')}}} placeholder="Message…" className="flex-1 text-[7px] rounded-lg border border-slate-200 px-2 py-1 outline-none bg-slate-50" />
        <button className="text-[7px] font-bold bg-chalkboard text-white rounded-lg px-2">Send</button>
      </div>
    </div>
  )
}

function ChatDesktop() {
  const [msgs, setMsgs] = useState(MSGS_INIT)
  const [draft, setDraft] = useState('')
  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-chalkboard flex items-center justify-center text-[7px] font-bold text-white">CG</div>
        <div>
          <div className="text-[10px] font-bold text-slate-800">Chosen Gold — Team Chat</div>
          <div className="text-[8px] text-slate-500">12 members</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {msgs.map((m,i) => (
          <div key={i} className={`flex gap-2 ${m.from==='You'?'flex-row-reverse':''}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold shrink-0 ${m.coach?'bg-chalkboard text-white':'bg-slate-200 text-slate-600'}`}>{m.init}</div>
            <div className={`max-w-[75%] rounded-xl px-2.5 py-2 text-[9px] leading-snug ${m.from==='You'?'bg-chalkboard text-white':'bg-slate-100 text-slate-800'}`}>{m.text}</div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 border-t border-slate-100 flex gap-2">
        <input value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&draft.trim()){setMsgs(m=>[...m,{from:'You',init:'ME',coach:true,text:draft.trim(),time:'Now'}]);setDraft('')}}} placeholder="Message the team…" className="flex-1 text-[9px] rounded-lg border border-slate-200 px-2.5 py-1.5 outline-none bg-slate-50" />
        <button className="text-[9px] font-bold bg-chalkboard text-white rounded-lg px-3">Send</button>
      </div>
    </div>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ id, flip, featured = false, eyebrow, eyeColor, heading, desc, bullets, cta, active, phone, desktop }: {
  id: string; flip?: boolean; featured?: boolean; eyebrow: string; eyeColor: string
  heading: React.ReactNode; desc: string; bullets: string[]; cta: string
  active: string; phone: React.ReactNode; desktop: React.ReactNode
}) {
  return (
    // `featured` = a warm-white tinted band + larger heading — a punctuation
    // moment that breaks the alternating white-flip rhythm of the feature stack.
    <section id={id} className="py-20 px-6 lg:px-12" style={{ borderBottom: '1px solid #EAECF0', background: featured ? '#FAFAF8' : undefined }}>
      <div className="max-w-[1100px] mx-auto flex flex-col items-center gap-12 lg:gap-16" style={{ flexDirection: flip ? undefined : undefined }}>
        <div className={`flex flex-col ${flip ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 lg:gap-16 w-full`}>
          <div className={`flex-1 ${featured ? 'max-w-[440px]' : 'max-w-[380px]'} shrink-0`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] mb-3" style={{ color: eyeColor }}>{eyebrow}</div>
            <h2 className={`font-black text-app-card leading-[1.08] tracking-[-1.5px] mb-4 ${featured ? 'text-[36px] sm:text-[44px]' : 'text-[30px] sm:text-[36px]'}`}>{heading}</h2>
            <p className="text-[14px] text-slate-500 leading-[1.65] mb-5">{desc}</p>
            <Bullets items={bullets} />
            <Link href="/signup" className="inline-flex items-center gap-1.5 bg-chalkboard text-white text-[13px] font-bold rounded-full px-6 py-2.5 hover:bg-chalkboard transition-colors">
              {cta} →
            </Link>
          </div>
          <Split active={active} phone={phone} desktop={desktop} />
        </div>
      </div>
    </section>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ForCoachesPage() {
  return (
    <div className="bg-white min-h-screen">
      <MarketingNav />

      {/* Hero */}
      <div data-section="hero" className="relative overflow-hidden pt-28 pb-20 px-6 text-center bg-chalkboard-grad">
        <HeroBackground />
        {/* 2026-05-21 simplification — copy reset around Field Mode + roster +
         *  schedule + payments + chat. No "AI announcer," no "live broadcasting,"
         *  no per-sport stat detail claims. Game Day is now a simple scoreboard
         *  and stays a feature, not the hero. */}
        <MarketingDisplayH1 align="center" tone="light" eyebrow="Built for coaches" className="mb-5">
          <span className="block">Run practice.</span>
          <span className="block" style={{ color: '#F5E642' }}>Run the team.</span>
        </MarketingDisplayH1>
        <p className="text-[16px] max-w-[500px] mx-auto mb-8" style={{ color: 'rgba(237,232,220,0.5)' }}>
          Field Mode on the sideline, RSVPs ahead of game day, dues collected without chasing — and a simple Game Day scoreboard parents can follow.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Link href="/signup" className="bg-chalk-yellow-hover text-chalk-dark text-[14px] font-black rounded-full px-8 py-3.5 hover:bg-chalk-yellow transition-colors">Start free →</Link>
          <a href="#game-day" className="text-[14px] font-medium rounded-full px-6 py-3.5 transition-all" style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(237,232,220,0.7)', border: '1px solid rgba(255,255,255,0.12)' }}>See features ↓</a>
        </div>
      </div>

      <SubNav />

      {/* 2026-05-21 — Game Day demoted. Per-sport stat detail, AI announcer,
       *  pitch-count tracking copy all removed. The remaining product is a
       *  simple +/- scoreboard that parents can follow. */}
      <Section id="game-day" featured eyebrow="Game Day" eyeColor="#13201B"
        heading={<>A scoreboard that<br />stays out of the way.</>}
        desc="Simple home / away scoring for every sport. One tap to bump the score, one tap to fix a mistake. Parents see the number update in real time — no separate broadcast app, no setup before the first inning."
        bullets={['Simple +/- scoreboard for every sport','Score updates parents see in their app instantly','No per-sport learning curve — same UI every weekend','Works on phone, tablet, or web — whichever you grabbed','Free on every tier — coaches do not lose this mid-game']}
        cta="Try Game Day free" active="Game Day"
        phone={<GameDayPhone />}
        desktop={<GameDayDesktop />}
      />

      <Section id="roster" flip eyebrow="Roster" eyeColor="#047857"
        heading={<>Every player.<br />Every contact.</>}
        desc="Player profiles with guardian contacts, emergency info, and waiver status. No more spreadsheets, no more lost forms, no parent contact info leaking to the rest of the team."
        bullets={['Player profiles with photo, position, jersey number','Guardian contacts visible to the coach only','Digital waiver collection and signature tracking','Roster export for tournament check-in','One source of truth — phone, tablet, or web']}
        cta="Build your roster" active="Roster"
        phone={<RosterPhone />}
        desktop={<RosterDesktop />}
      />

      <Section id="schedule" eyebrow="Schedule" eyeColor="#1d4ed8"
        heading={<>Know who&apos;s coming<br />before game day.</>}
        desc="Create games and practices in seconds. Parents RSVP in the app. You see the headcount in real time — no more day-of surprises, no separate group chat poll."
        bullets={['Games, practices, and custom events','RSVP — going / not going / maybe','Real-time headcount before each event','Add to Apple or Google Calendar','Location with map link, weather, and notes']}
        cta="Set up your schedule" active="Schedule"
        phone={<SchedulePhone />}
        desktop={<ScheduleDesktop />}
      />

      <Section id="payments" flip eyebrow="Payments" eyeColor="#13201B"
        heading={<>Collect dues.<br />Stop chasing parents.</>}
        desc="Create a payment request in 30 seconds. Parents pay in-app, money lands in your Stripe account, reminders go out automatically. Parents are never asked to subscribe to use Chalk — they just pay the team."
        bullets={['Dues, fees, registrations — one request flow','Parents pay by card. No cash, no Venmo, no group chat math','Automated reminders for unpaid invoices','Real-time collection progress per request','Instant payouts to your bank via Stripe Connect']}
        cta="Start collecting dues" active="Payments"
        phone={<PaymentsPhone />}
        desktop={<PaymentsDesktop />}
      />

      {/* 2026-05-21 — chat section reset. "AI Assistant" surface was killed
       *  earlier (K13) and remains killed. Parent-to-parent communication is
       *  intentionally limited to the team thread + 1:1 Chat DM — no contact
       *  info exposure under Q4 Option D. */}
      <Section id="chat" eyebrow="Chat" eyeColor="#6d28d9"
        heading={<>One thread.<br />Everyone on the same page.</>}
        desc="Team thread where coaches and parents talk. Pin announcements that need to stick. Coach-only staff thread for the conversations parents do not need to see. Parents reach each other through Chat DM — never through exposed phone numbers or email."
        bullets={['Team-wide thread and 1:1 direct messages','Pinned announcements that stay at the top','File and photo sharing','Coach-only staff side-channel','Parent contact info stays coach-only — DMs only via Chat']}
        cta="Start messaging your team" active="Chat"
        phone={<ChatPhone />}
        desktop={<ChatDesktop />}
      />

      {/* CTA */}
      <section className="py-24 px-6 text-center bg-chalkboard-grad">
        <h2 className="text-[38px] sm:text-[48px] font-black text-white tracking-[-2px] mb-4">Ready for the season?</h2>
        <p className="text-[16px] mb-8 max-w-[440px] mx-auto" style={{ color: 'rgba(237,232,220,0.5)' }}>Free to start. No credit card. Set up your team in under 5 minutes. Upgrade when Field Mode earns its keep.</p>
        <Link href="/signup" className="inline-flex items-center bg-chalk-yellow-hover text-chalk-dark text-[15px] font-black rounded-full px-10 py-4 hover:bg-chalk-yellow transition-colors">Start free →</Link>
      </section>

      <HomepageFooter />
    </div>
  )
}
